import { useMemo } from "react";
import { get } from "lodash";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { queryClient } from "App";
import { formatError } from "helpers/formatError";
import { addResizedColumns } from "hooks/useTableConfig";
import { getCallDispositionsTableColumns } from "routes/call_dispositions/components/call_dispositions_table/CallDispositionsTableColumnsAndFilters";
import { CALL_DISPOSITIONS_TABLE_CONSTANTS } from "routes/call_dispositions/components/call_dispositions_table/CallDispositionsTableV3";
import CallDispositionService from "services/CallDispositions";
import UserService from "services/User";

export const invalidateCallDispositionsQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [CALL_DISPOSITIONS_TABLE_CONSTANTS.queryKey],
  });
};

const invalidateDispositionQueries = async () => {
  await invalidateCallDispositionsQuery();
  // The parent options are read from this page-level query rather than
  // refetched when the form opens, so a new or renamed parent only shows up
  // if we invalidate it alongside the table.
  await queryClient.invalidateQueries({ queryKey: ["parent-dispositions"] });
};

export const useCallDispositionsMutations = ({ onDeleted } = {}) => {
  const create = useMutation({
    mutationFn: data => CallDispositionService.createDisposition(data),
    onSuccess: () => invalidateDispositionQueries(),
    onError: err => toast.error(formatError(err, "Failed to save disposition")),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) =>
      CallDispositionService.editDisposition(id, data),
    onSuccess: () => invalidateDispositionQueries(),
    onError: err => toast.error(formatError(err, "Failed to save disposition")),
  });

  const remove = useMutation({
    mutationFn: id => CallDispositionService.deleteDisposition(id),
    onSuccess: () => {
      invalidateDispositionQueries();
      toast.success("Disposition deleted");
      onDeleted?.();
    },
    onError: err =>
      toast.error(formatError(err, "Failed to delete disposition")),
  });

  return { create, update, remove };
};

// `for_filters` is unpaginated, so tenants with a large number of top-level
// dispositions still get every parent option.
const fetchParentDispositions = () =>
  CallDispositionService.getDispositionsForFilters({ has_parent: "false" });

export const useCallDispositionsTable = () => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });
  const { data: parentDispositions, isSuccess: isParentDispositionsFetched } =
    useQuery({
      queryKey: ["parent-dispositions"],
      queryFn: fetchParentDispositions,
    });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched || !isParentDispositionsFetched) return [];
    const initialColumns = getCallDispositionsTableColumns({
      parentDispositions,
    });
    const callDispositionsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${CALL_DISPOSITIONS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: callDispositionsTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [
    isTableSettingsFetched,
    isParentDispositionsFetched,
    parentDispositions,
    tableSettings,
  ]);

  return {
    columns,
    parentDispositions,
  };
};
